/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 文件夹状态管理 (RTK Slice)
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// 照片类型
interface Photo {
  seq: string | number
  id?: string
  file?: string
  smallURL?: string
  largeURL?: string
  time?: number
  userID?: string | number
  isLimitViewByPhotoCount?: boolean
  [key: string]: any
}

// 选中照片的 Map 类型（key 为 seq）
type CheckedPhotosMap = Record<string | number, Photo>

// State 类型
interface FolderState {
  photos: Photo[]
  totalFolderCount: number
  defaultFolderCount: number
  checkAll: boolean
  photosNum: number
  checkedPhotos: CheckedPhotosMap
  unCheckedPhotos: CheckedPhotosMap
  detailPageLoading: boolean
}

const initialState: FolderState = {
  photos: [],
  totalFolderCount: 0,
  defaultFolderCount: 0,
  checkAll: false,
  photosNum: 0,
  checkedPhotos: {},
  unCheckedPhotos: {},
  detailPageLoading: false
}

const folderSlice = createSlice({
  name: 'folder',
  initialState,
  reducers: {
    setDetailPageLoading(state, action: PayloadAction<boolean>) {
      state.detailPageLoading = action.payload
    },

    setPhotos(state, action: PayloadAction<Photo[]>) {
      state.photos = action.payload
    },

    setPhotosNum(state, action: PayloadAction<number>) {
      state.photosNum = action.payload
    },

    setTotalFolderCount(state, action: PayloadAction<number>) {
      state.totalFolderCount = action.payload
    },

    setDefaultFolderCount(state, action: PayloadAction<number>) {
      state.defaultFolderCount = action.payload
    },

    setCheckAll(state, action: PayloadAction<boolean>) {
      state.checkAll = action.payload
    },

    clearCheckedPhotos(state) {
      state.checkedPhotos = {}
    },

    clearUnCheckedPhotos(state) {
      state.unCheckedPhotos = {}
    },

    pushCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      const seq = photo.seq
      state.checkedPhotos[seq] = photo
    },

    pushUnCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      const seq = photo.seq
      state.unCheckedPhotos[seq] = photo
    },

    removeCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      const seq = photo.seq
      delete state.checkedPhotos[seq]
    },

    removeUnCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      const seq = photo.seq
      delete state.unCheckedPhotos[seq]
    }
  },
  extraReducers: (builder) => {
    // 兼容旧的 dispatch({ type: actionTypes.XXX, data: ... }) 调用
    builder
      .addMatcher(
        (action) => action.type === 'FOLDER_CLEAR_CHECKED_PHOTO',
        (state) => {
          state.checkedPhotos = {}
        }
      )
      .addMatcher(
        (action) => action.type === 'FOLDER_CLEAR_UNCHECKED_PHOTO',
        (state) => {
          state.unCheckedPhotos = {}
        }
      )
      .addMatcher(
        (action) => action.type === 'FOLDER_SET_CHECK_ALL',
        (state, action: any) => {
          state.checkAll = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_FOLDERS',
        (state, action: any) => {
          state.photos = action.data || []
        }
      )
  }
})

export const {
  setDetailPageLoading,
  setPhotos,
  setPhotosNum,
  setTotalFolderCount,
  setDefaultFolderCount,
  setCheckAll,
  clearCheckedPhotos,
  clearUnCheckedPhotos,
  pushCheckedPhoto,
  pushUnCheckedPhoto,
  removeCheckedPhoto,
  removeUnCheckedPhoto
} = folderSlice.actions

export default folderSlice.reducer
